import { useState } from "react";
import type { Customer } from "@/types";
import { formatMoney, formatQty } from "@/lib/format";
import type { CartItem, CartTotals } from "@/pages/pos/useCart";

interface CartPanelProps {
  items: CartItem[];
  totals: CartTotals;
  canDiscount: boolean;
  customer: Customer | null;
  onOpenCustomer: () => void;
  onIncrement: (key: string) => void;
  onDecrement: (key: string) => void;
  onSetQty: (key: string, qty: number) => void;
  onSetDiscount: (key: string, discount: number) => void;
  onRemove: (key: string) => void;
  onCheckout: () => void;
  onClear: () => void;
  onBrowse: () => void;
  onSuspend: () => void;
  onOpenHeld: () => void;
  heldCount: number;
}

export default function CartPanel({
  items,
  totals,
  canDiscount,
  customer,
  onOpenCustomer,
  onIncrement,
  onDecrement,
  onSetQty,
  onSetDiscount,
  onRemove,
  onCheckout,
  onClear,
  onBrowse,
  onSuspend,
  onOpenHeld,
  heldCount,
}: CartPanelProps) {
  const [editingDiscount, setEditingDiscount] = useState<string | null>(null);

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100">
      <div className="flex flex-col gap-3 border-b border-background-200 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-shopping-cart-2-line text-base"></i>
            </span>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Carrito ({formatQty(totals.unitCount)})
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onBrowse}
              className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 text-xs font-semibold text-foreground-700 lg:hidden"
            >
              <i className="ri-store-2-line text-base"></i>
              Catálogo
            </button>
            <button
              type="button"
              onClick={onClear}
              disabled={items.length === 0}
              className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-2.5 text-xs font-semibold text-primary-700 disabled:opacity-40"
            >
              <i className="ri-delete-bin-line text-base"></i>
              Vaciar
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onOpenCustomer}
          className="flex items-center gap-3 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-left transition-colors hover:border-secondary-300 hover:bg-secondary-100/60"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
            <i className="ri-user-3-line text-base"></i>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-foreground-900">
              {customer ? `${customer.first_name} ${customer.last_name}` : "Consumidor final"}
            </span>
            <span className="block text-[11px] text-foreground-500">
              {customer
                ? customer.customer_type === "mayorista"
                  ? "Precio mayorista aplicado"
                  : "Cliente regular"
                : "Toca para elegir un cliente"}
            </span>
          </span>
          <i className="ri-arrow-right-up-line text-base text-foreground-400"></i>
        </button>
      </div>

      <div className="rkc-scroll-thin min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-shopping-basket-2-line text-2xl"></i>
            </span>
            <p className="text-sm text-foreground-500">
              El carrito está vacío.
              <br />
              Busca o escanea un producto para empezar.
            </p>
            <button
              type="button"
              onClick={onBrowse}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 lg:hidden"
            >
              <i className="ri-add-line text-base"></i>
              Agregar productos
            </button>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((item) => (
              <li key={item.key} className="rounded-md border border-background-200 bg-background-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-semibold text-foreground-900">{item.name}</span>
                      {item.offer_applied ? (
                        <span className="shrink-0 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                          Oferta
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-[11px] text-foreground-500">
                      {formatMoney(item.unit_price)} · {item.unit ?? ""}
                      {item.stock > 0 && item.item_type === "product" && item.quantity > item.stock ? (
                        <span className="ml-1 font-semibold text-primary-600">(stock: {formatQty(item.stock)})</span>
                      ) : null}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(item.key)}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                  >
                    <i className="ri-close-line text-base"></i>
                  </button>
                </div>

                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 rounded-md border border-background-300 bg-background-50 p-0.5">
                    <button
                      type="button"
                      onClick={() => onDecrement(item.key)}
                      className="flex h-7 w-7 items-center justify-center rounded text-foreground-700 hover:bg-background-200"
                    >
                      <i className="ri-subtract-line text-base"></i>
                    </button>
                    <input
                      value={formatQty(item.quantity)}
                      onChange={(event) => {
                        const parsed = Number(event.target.value.replace(/[^0-9.]/g, ""));
                        onSetQty(item.key, Number.isFinite(parsed) ? parsed : 1);
                      }}
                      inputMode="decimal"
                      className="w-12 bg-transparent text-center text-sm font-semibold text-foreground-900 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => onIncrement(item.key)}
                      className="flex h-7 w-7 items-center justify-center rounded text-foreground-700 hover:bg-background-200"
                    >
                      <i className="ri-add-line text-base"></i>
                    </button>
                  </div>
                  <span className="font-heading text-sm font-bold text-foreground-950">
                    {formatMoney(Math.max(item.quantity * item.unit_price - item.discount, 0))}
                  </span>
                </div>

                {canDiscount ? (
                  <div className="mt-2">
                    {editingDiscount === item.key || item.discount > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-foreground-500">Descuento RD$</span>
                        <input
                          value={item.discount ? String(item.discount) : ""}
                          onChange={(event) => {
                            const parsed = Number(event.target.value.replace(/[^0-9.]/g, ""));
                            onSetDiscount(item.key, Number.isFinite(parsed) ? parsed : 0);
                          }}
                          inputMode="decimal"
                          placeholder="0.00"
                          className="w-20 rounded border border-background-300 bg-background-50 px-2 py-1 text-xs text-foreground-900 outline-none focus:border-primary-400"
                        />
                        <button
                          type="button"
                          onClick={() => setEditingDiscount(null)}
                          className="text-[11px] font-semibold text-primary-600"
                        >
                          Listo
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingDiscount(item.key)}
                        className="flex items-center gap-1.5 text-[11px] font-semibold text-secondary-800 hover:text-secondary-900"
                      >
                        <i className="ri-percent-line text-sm"></i>
                        Aplicar descuento
                      </button>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-background-200 bg-background-100 p-4">
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-foreground-500">Subtotal</dt>
            <dd className="font-medium text-foreground-900">{formatMoney(totals.subtotal)}</dd>
          </div>
          {totals.discountTotal > 0 ? (
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Descuento</dt>
              <dd className="font-medium text-primary-600">-{formatMoney(totals.discountTotal)}</dd>
            </div>
          ) : null}
          {totals.taxTotal > 0 ? (
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Impuestos</dt>
              <dd className="font-medium text-foreground-900">{formatMoney(totals.taxTotal)}</dd>
            </div>
          ) : null}
          <div className="mt-1 flex items-center justify-between border-t border-background-200 pt-2.5">
            <dt className="font-heading text-sm font-semibold text-foreground-950">Total</dt>
            <dd className="font-heading text-xl font-bold text-primary-600">{formatMoney(totals.total)}</dd>
          </div>
        </dl>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onSuspend}
            disabled={items.length === 0}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <i className="ri-pause-circle-line text-base"></i>
            Suspender
          </button>
          <button
            type="button"
            onClick={onOpenHeld}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-100 px-3 py-2.5 text-xs font-semibold text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            <i className="ri-inbox-archive-line text-base"></i>
            Recuperar{heldCount > 0 ? ` (${heldCount})` : ""}
          </button>
        </div>

        <button
          type="button"
          onClick={onCheckout}
          disabled={items.length === 0}
          className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <i className="ri-secure-payment-line text-lg"></i>
          Cobrar {formatMoney(totals.total)}
        </button>
      </div>
    </section>
  );
}